import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

export type GenerationStepStatus = 'pending' | 'in-progress' | 'completed' | 'error';

export interface GenerationProgressStep {
  id: string;
  message: string;
  status: GenerationStepStatus;
  error?: string;
}

export interface GenerationBatchProgress {
  current: number;
  total: number;
}

export interface GenerationBatchSummary {
  success: number;
  failed: number;
  skipped: number;
  total: number;
  wasCancelled: boolean;
}

export interface EditorGenerationDomain {
  showProgressModal: boolean;
  progressSteps: GenerationProgressStep[];
  isCancellingGeneration: boolean;
  batchProgress: GenerationBatchProgress | null;
  batchSummary: GenerationBatchSummary | null;
  generatingPageNumber: number | null;
  cancellationRef: React.MutableRefObject<boolean>;
  busyRef: React.MutableRefObject<boolean>;
  batchActiveRef: React.MutableRefObject<boolean>;
  setProgressSteps: Dispatch<SetStateAction<GenerationProgressStep[]>>;
  updateProgressStep: (id: string, status: Exclude<GenerationStepStatus, 'pending'>, detail?: string) => void;
  startPage: (steps: GenerationProgressStep[], pageNumber: number) => void;
  requestCancellation: () => void;
  startBatch: (total: number) => void;
  trackBatchPage: (current: number, total: number) => void;
  finishBatch: (summary: GenerationBatchSummary) => void;
  resetProgress: () => void;
}

export const updateGenerationStep = (
  steps: GenerationProgressStep[],
  id: string,
  status: Exclude<GenerationStepStatus, 'pending'>,
  detail?: string
): GenerationProgressStep[] => steps.map(step => (
  step.id === id ? { ...step, status, error: detail } : step
));

export const cancelGenerationSteps = (
  steps: GenerationProgressStep[]
): GenerationProgressStep[] => steps.map(step => (
  step.status === 'in-progress'
    ? { ...step, status: 'error', error: 'Cancelled by user' }
    : step
));

export const useEditorGenerationDomain = (): EditorGenerationDomain => {
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressSteps, setProgressSteps] = useState<GenerationProgressStep[]>([]);
  const [isCancellingGeneration, setIsCancellingGeneration] = useState(false);
  const [batchProgress, setBatchProgress] = useState<GenerationBatchProgress | null>(null);
  const [batchSummary, setBatchSummary] = useState<GenerationBatchSummary | null>(null);
  const [generatingPageNumber, setGeneratingPageNumber] = useState<number | null>(null);
  const cancellationRef = useRef(false);
  const busyRef = useRef(false);
  const batchActiveRef = useRef(false);

  const updateProgressStep = useCallback<EditorGenerationDomain['updateProgressStep']>(
    (id, status, detail) => setProgressSteps(current => (
      updateGenerationStep(current, id, status, detail)
    )),
    []
  );

  const startPage = useCallback((steps: GenerationProgressStep[], pageNumber: number) => {
    busyRef.current = true;
    setProgressSteps(steps);
    setGeneratingPageNumber(pageNumber);
    setBatchSummary(null);
    setShowProgressModal(true);
  }, []);

  const requestCancellation = useCallback(() => {
    cancellationRef.current = true;
    setIsCancellingGeneration(true);
    setProgressSteps(cancelGenerationSteps);
  }, []);

  const startBatch = useCallback((total: number) => {
    busyRef.current = true;
    batchActiveRef.current = true;
    cancellationRef.current = false;
    setIsCancellingGeneration(false);
    setBatchSummary(null);
    setBatchProgress({ current: total > 0 ? 1 : 0, total });
  }, []);

  const trackBatchPage = useCallback((current: number, total: number) => {
    setBatchProgress({ current, total });
  }, []);

  const finishBatch = useCallback((summary: GenerationBatchSummary) => {
    busyRef.current = false;
    batchActiveRef.current = false;
    cancellationRef.current = false;
    setIsCancellingGeneration(false);
    setBatchProgress(null);
    setBatchSummary(summary);
  }, []);

  const resetProgress = useCallback(() => {
    setShowProgressModal(false);
    setIsCancellingGeneration(false);
    setBatchProgress(null);
    setBatchSummary(null);
    setGeneratingPageNumber(null);
  }, []);

  return {
    showProgressModal,
    progressSteps,
    isCancellingGeneration,
    batchProgress,
    batchSummary,
    generatingPageNumber,
    cancellationRef,
    busyRef,
    batchActiveRef,
    setProgressSteps,
    updateProgressStep,
    startPage,
    requestCancellation,
    startBatch,
    trackBatchPage,
    finishBatch,
    resetProgress
  };
};
